import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode, useCallback, useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export enum PanelSide {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum SidepanelSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  FULL = 'full',
}

/**
 * The width of the sidepanel in pixels.
 *
 * For any size other than FULL, use numbers so that the value can be used
 * in calculations.
 */
export const sizeToWidth: Record<SidepanelSize, string | number> = {
  [SidepanelSize.SMALL]: 240,
  [SidepanelSize.MEDIUM]: 480,
  [SidepanelSize.FULL]: '100%',
};

export interface FlowchartContextPanelProps {
  panelSide: PanelSide;
  isOpen: boolean;
  size?: SidepanelSize;
  onClose: () => void;
  sidepanelName: string;
  sidepanelIcon?: BenchlingIconName;
  /** Content to render inside the sidepanel */
  children: ReactNode;
}

function getExpandCollapseButton(
  inputSize: SidepanelSize,
  currentPanelSize: SidepanelSize,
  handleExpand: () => void,
  handleCollapse: () => void
): JSX.Element | null {
  if (inputSize === SidepanelSize.FULL) {
    // Assume we can't collapse a full-width sidepanel for readability purposes
    // And we can't expand a full-width sidepanel further, so no buttons needed.
    return null;
  }

  if (currentPanelSize === SidepanelSize.FULL) {
    return (
      <button
        onClick={handleCollapse}
        aria-label="Collapse panel"
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'collapseButton',
        })}
        className={css(styles.expandButton)}
        type="button"
      >
        <BenchlingIcon icon="collapse-right-full" />
      </button>
    );
  }

  return (
    <button
      onClick={handleExpand}
      aria-label="Expand panel"
      {...getTestProps({
        component: 'FlowchartContextPanel',
        element: 'expandButton',
      })}
      className={css(styles.expandButton)}
      type="button"
    >
      <BenchlingIcon icon="expand-right-full" />
    </button>
  );
}

function HeaderButtons(props: {
  panelSide: PanelSide;
  isOpen: boolean;
  inputSize: SidepanelSize;
  currentPanelSize: SidepanelSize;
  onClose: () => void;
  handleExpand: () => void;
  handleCollapse: () => void;
}): JSX.Element | null {
  if (props.panelSide === PanelSide.LEFT) {
    return (
      <div className={css(styles.headerButtons)}>
        <button
          onClick={props.onClose}
          aria-label="Close panel"
          {...getTestProps({
            component: 'FlowchartContextPanel',
            element: 'closeButton',
          })}
          className={css(styles.closeButton)}
          type="button"
        >
          <BenchlingIcon icon="chevron-left" />
        </button>
      </div>
    );
  }

  return (
    <div className={css(styles.headerButtons)}>
      {getExpandCollapseButton(
        props.inputSize,
        props.currentPanelSize,
        props.handleExpand,
        props.handleCollapse
      )}
      <button
        onClick={props.onClose}
        aria-label="Close panel"
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'closeButton',
        })}
        className={css(styles.closeButton)}
        type="button"
      >
        <BenchlingIcon icon="close" />
      </button>
    </div>
  );
}
function getStyleForSize(size: SidepanelSize): {} {
  switch (size) {
    case SidepanelSize.FULL:
      return styles.sizeFull;
    case SidepanelSize.SMALL:
      return styles.sizeSmall;
    case SidepanelSize.MEDIUM:
    default:
      return styles.sizeMedium;
  }
}

/**
 * A sidepanel to use with ReactFlow flowcharts, supports left and right side panels
 * at different widths (including full width).
 *
 * Can use with @link{FlowchartSidebar}.
 *
 * @example
 * <FlowchartContextPanel
 *   panelSide={PanelSide.RIGHT}
 *   isOpen={isOpen}
 *   size={SidepanelSize.FULL}
 *   onClose={() => setIsOpen(false)}
 * >
 *   Your content here
 * </FlowchartContextPanel>
 *
 * @example
 * <FlowchartContextPanel
 *   panelSide={PanelSide.LEFT}
 *   isOpen={isOpen}
 *   size={SidepanelSize.MEDIUM}
 *   onClose={() => setIsOpen(false)}
 * >
 *   Your content here
 * </FlowchartContextPanel>
 */
export function FlowchartContextPanel({
  panelSide,
  isOpen,
  size = SidepanelSize.MEDIUM,
  onClose,
  children,
  sidepanelName,
  sidepanelIcon,
}: FlowchartContextPanelProps): JSX.Element | null {
  const [panelSize, setPanelSize] = useState<SidepanelSize>(size);

  const handleExpand = useCallback(() => {
    setPanelSize(SidepanelSize.FULL);
  }, [setPanelSize]);

  const handleCollapse = useCallback(() => {
    setPanelSize(size);
  }, [setPanelSize, size]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const sizeStyle = getStyleForSize(panelSize);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={css(
        styles.sidepanel,
        sizeStyle,
        panelSide === PanelSide.RIGHT && styles.rightSidepanel,
        panelSide === PanelSide.LEFT && styles.leftSidepanel
      )}
      {...getTestProps({
        component: 'FlowchartContextPanel',
        element: 'root',
      })}
    >
      <div
        className={css(styles.header)}
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'header',
        })}
      >
        <div
          className={css(styles.headerLeft)}
          {...getTestProps({
            component: 'FlowchartContextPanel',
            element: 'headerLeft',
          })}
        >
          {sidepanelIcon && <BenchlingIcon icon={sidepanelIcon} />}
          <h2
            className={css(styles.sidepanelName)}
            {...getTestProps({
              component: 'FlowchartContextPanel',
              element: 'sidepanelName',
            })}
          >
            {sidepanelName}
          </h2>
        </div>
        <HeaderButtons
          panelSide={panelSide}
          isOpen={isOpen}
          onClose={handleClose}
          inputSize={size}
          currentPanelSize={panelSize}
          handleExpand={handleExpand}
          handleCollapse={handleCollapse}
        />
      </div>
      <div
        className={css(styles.content)}
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'content',
        })}
      >
        {children}
      </div>
    </div>
  );
}

export default FlowchartContextPanel;

const styles = StyleSheet.create({
  sidepanel: {
    height: '100%',
    backgroundColor: Color.WHITE,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    top: 0,
    zIndex: 10,
  },
  rightSidepanel: {
    borderLeft: `1px solid ${Color.GRAY_200}`,
    right: 0,
  },
  leftSidepanel: {
    borderRight: `1px solid ${Color.GRAY_200}`,
    left: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: `1px solid ${Color.GRAY_200}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sidepanelName: {
    margin: 0,
    fontSize: '13px',
    fontWeight: 600,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  sizeFull: {
    width: sizeToWidth[SidepanelSize.FULL],
  },
  sizeSmall: {
    width: sizeToWidth[SidepanelSize.SMALL],
  },
  sizeMedium: {
    width: sizeToWidth[SidepanelSize.MEDIUM],
  },
});
